import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Check, Loader, Shield, Zap, Star } from 'lucide-react'
import toast from 'react-hot-toast'

const SubscriptionPlan = ({ formData, onNext, isLoading, setParentPaymentCompleted }) => {
    const [billingCycle, setBillingCycle] = useState(formData.billing_cycle || 'Monthly')
    const [selectedPlan, setSelectedPlan] = useState(formData.plan_name || formData.selectedPlan || 'Pro')

    // Update parent form data when selection changes
    useEffect(() => {
        if (formData.plan_name !== selectedPlan || formData.billing_cycle !== billingCycle) {
            // We don't have a direct setFormData prop, but typically these forms update on next
            // However, keeping local state is fine until submission
        }
    }, [selectedPlan, billingCycle, formData])

    const plans = [
        {
            id: 'Basic',
            name: 'Basic',
            description: 'Essential features for small teams',
            monthlyPrice: 'Free',
            yearlyPrice: 'Free',
            features: [
                'Up to 3 Job Postings',
                'Basic Talent Search',
                'Email Support',
                'Community Access'
            ],
            icon: <Shield className="w-6 h-6" />,
            color: 'blue'
        },
        {
            id: 'Pro',
            name: 'Pro',
            description: 'Advanced tools for growing companies',
            monthlyPrice: '$49',
            yearlyPrice: '$470',
            features: [
                'Unlimited Job Postings',
                'Advanced Talent Search',
                'Priority Support',
                'Analytics Dashboard',
                'Team Collaboration'
            ],
            popular: true,
            icon: <Zap className="w-6 h-6" />,
            color: 'purple'
        },
        {
            id: 'Enterprise',
            name: 'Enterprise',
            description: 'Custom solutions for large organizations',
            monthlyPrice: 'Custom',
            yearlyPrice: 'Custom',
            features: [
                'Custom Job Postings Limit',
                'Dedicated Account Manager',
                'API Access',
                'Custom Integrations',
                'SLA Support',
                'White-labeling'
            ],
            icon: <Star className="w-6 h-6" />,
            color: 'orange'
        }
    ]

    const handleSubmit = (e) => {
        e.preventDefault()

        // Validate selection
        if (!selectedPlan) {
            toast.error('Please select a plan to continue')
            return
        }

        const planData = {
            ...formData,
            plan_name: selectedPlan,
            selectedPlan: selectedPlan,
            billing_cycle: billingCycle,
            amount: plans.find(p => p.id === selectedPlan)?.monthlyPrice === 'Free' ? 0 : 49, // Simplified logic
            currency: 'USD',
            payment_method: 'Card' // Default for now
        }

        onNext(planData)
    }

    // Expose submit method to parent via ref if needed, or rely on internal button
    // CompanyRegistration uses ref to call submitForm, lets implement that pattern if possible, 
    // but since we are a functional component, we'd need forwardRef. 
    // Looking at CompanyRegistration, it tries to find a form and dispatch submit.
    // So a standard form submit handler is good.

    return (
        <form onSubmit={handleSubmit} className="space-y-8">
            <div className="text-center space-y-4">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Choose Your Plan</h3>
                <p className="text-gray-600 dark:text-gray-400">Select the plan that best fits your needs</p>

                {/* Billing Cycle Toggle */}
                <div className="flex items-center justify-center mt-6">
                    <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-lg flex items-center">
                        <button
                            type="button"
                            onClick={() => setBillingCycle('Monthly')}
                            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${billingCycle === 'Monthly'
                                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                                }`}
                        >
                            Monthly
                        </button>
                        <button
                            type="button"
                            onClick={() => setBillingCycle('Yearly')}
                            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${billingCycle === 'Yearly'
                                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                                }`}
                        >
                            Yearly <span className="text-xs text-green-500 ml-1">(Save 20%)</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {plans.map((plan) => (
                    <div
                        key={plan.id}
                        onClick={() => setSelectedPlan(plan.id)}
                        className={`relative rounded-2xl border-2 p-6 cursor-pointer transition-all duration-200 hover:shadow-xl ${selectedPlan === plan.id
                                ? `border-${plan.color}-500 bg-${plan.color}-50 dark:bg-${plan.color}-900/10 active-plan-card`
                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                            } ${selectedPlan === plan.id ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
                    // Note: ring class added for better visibility of selection
                    >
                        {plan.popular && (
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                <span className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                                    MOST POPULAR
                                </span>
                            </div>
                        )}

                        <div className="space-y-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${selectedPlan === plan.id ? `bg-${plan.color}-500 text-white` : `bg-${plan.color}-100 text-${plan.color}-600`
                                }`}>
                                {plan.icon}
                            </div>

                            <div>
                                <h4 className="text-lg font-bold text-gray-900 dark:text-white">{plan.name}</h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{plan.description}</p>
                            </div>

                            <div className="flex items-baseline">
                                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                                    {billingCycle === 'Monthly' ? plan.monthlyPrice : plan.yearlyPrice}
                                </span>
                                {plan.monthlyPrice !== 'Custom' && plan.monthlyPrice !== 'Free' && (
                                    <span className="text-gray-500 dark:text-gray-400 ml-1">
                                        /{billingCycle === 'Monthly' ? 'mo' : 'yr'}
                                    </span>
                                )}
                            </div>

                            <ul className="space-y-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                                {plan.features.map((feature, index) => (
                                    <li key={index} className="flex items-start space-x-3">
                                        <Check className={`w-5 h-5 flex-shrink-0 ${selectedPlan === plan.id ? `text-${plan.color}-600` : 'text-green-500'
                                            }`} />
                                        <span className="text-sm text-gray-600 dark:text-gray-300">{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="pt-4">
                                <div className={`w-full py-2 rounded-lg text-center text-sm font-semibold transition-colors ${selectedPlan === plan.id
                                        ? `bg-${plan.color}-600 text-white`
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
                                    }`}>
                                    {selectedPlan === plan.id ? 'Selected' : 'Select Plan'}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Hidden button to allow form submission via Enter key or external trigger */}
            <button type="submit" className="hidden" />
        </form>
    )
}

export default SubscriptionPlan
